/**
 * confirmDelete — shared destructive-action confirmation dialog.
 *
 * Usage (plain module, not a hook — call from event handlers):
 *
 *   import confirmDelete from './confirmDelete';
 *   // Inside an async handler where globals is available:
 *   const ok = await confirmDelete(
 *     { toggleMessageBox, getMsgBoxResult },
 *     { title: 'Delete vehicle?', message: 'This cannot be undone.', counts: { payments: 3, images: 5 } }
 *   );
 *   if (ok) { ... }
 *
 * Pattern followed: useDiscardGuard.js (getMsgBoxResult promise-style confirm).
 * MessageBox contract (Cars.jsx:157-161, MessageBox.jsx:30):
 *   - getMsgBoxResult({ on:true, title, message, buttons }) → Promise<string>
 *     resolves to the label of the button the user clicked.
 *   - buttons[0] is the primary (affirmative, rightmost); rest are secondaries.
 *   - The resolved string is compared directly to the label to derive a boolean.
 *
 * @param {{ toggleMessageBox: Function, getMsgBoxResult: Function }} msgApi
 *   Destructured from the GlobalContext (or globals object in class-style components).
 *
 * @param {{ title?: string, message?: string, counts?: Object.<string,number> }} opts
 *   title    — Dialog title. Defaults to "Confirm Delete".
 *   message  — Body message. Defaults to "This action cannot be undone."
 *   counts   — Optional map of child record types to counts, rendered as a
 *              "This will also delete:" line, e.g. { payments: 3, images: 5 }
 *              produces "This will also delete: 3 payments, 5 images."
 *
 * @returns {Promise<boolean>} Resolves true only if the user clicks "Delete".
 */
export default async function confirmDelete(
  { getMsgBoxResult },
  { title = "Confirm Delete", message = "This action cannot be undone.", counts } = {}
) {
  let fullMessage = message;

  if (counts && typeof counts === "object") {
    const entries = Object.entries(counts).filter(([, n]) => Number(n) > 0);
    if (entries.length > 0) {
      const list = entries
        .map(([label, n]) => `${n} ${label}`)
        .join(", ");
      fullMessage = `${message}\n\nThis will also delete: ${list}.`;
    }
  }

  const choice = await getMsgBoxResult({
    on: true,
    title,
    message: fullMessage,
    buttons: ["Delete", "Cancel"],
  });

  return choice === "Delete";
}
